import type { AnchorHTMLAttributes } from 'react';
import classNames from 'classnames';
import { useFela } from 'react-fela';
import { NavLink as RouterLink, type NavLinkProps as RouteLinkProps } from 'react-router-dom-v5-compat';

import type { Theme } from '@/styles/theme';

import { container, externalLink, LinkType, type LinkSize } from './Link.rules';

interface CommonProps {
    size?: `${LinkSize}`;
    color?: keyof Theme['colors'];
    inheritColor?: boolean;
    customStyle?: typeof container;
}

export interface InternalLinkProps extends Omit<RouteLinkProps, 'color'>, CommonProps {
    type: 'router';
    overrideActiveClassName?: boolean;
}

export interface ExternalLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>, CommonProps {
    type: 'native';
    noreferrer?: boolean;
}

export const Link = (props: InternalLinkProps | ExternalLinkProps) => {
    const { css } = useFela({
        size: props.size,
        color: props.color,
        inheritColor: props.inheritColor,
    });

    if (props.type === LinkType.ROUTER) {
        const { customStyle, className, type, size, color, overrideActiveClassName, ...rest } = props;
        return <RouterLink className={classNames('Link', css(container, customStyle), { active: overrideActiveClassName })} {...rest} />;
    }

    const { customStyle, className, type, size, color, children, inheritColor, noreferrer = false, ...rest } = props;
    return (
        <a
            className={`Link ${css(externalLink, customStyle)}`}
            target='_blank'
            rel={noreferrer ? 'noopener noreferrer' : 'noopener'}
            {...rest}
        >
            {children}
        </a>
    );
};
